import { AlertProps, IconButton } from "@alm/flow-ds/components";
import BusinessLogicContext, {
	BusinessLogicContextProps,
} from "contexts/BusinessLogicContext";
import {
	businessLogicPropertyType,
	BusinessLogicUpdateData,
	FormBusinessLogicUpdateDataValidation,
} from "hooks/FormDesigner/useElementList";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { businessLogicValue } from "schemas/elements";
import {
	isBusinessLogicValid,
	runFormBusinessLogicValidation,
} from "validation/validation";
import BusinessLogic from "./BusinessLogic";
interface Props {
	updateStore: (
		data: BusinessLogicUpdateData<businessLogicPropertyType>
	) => void;
	elementIndex: number;
	id: string;
	value?: businessLogicValue;
	action: businessLogicPropertyType;
	validation: boolean;
	updateFormBusinessLogicValidation: (
		data: FormBusinessLogicUpdateDataValidation
	) => void;
	addAlert: (alert: string, status: AlertProps["status"]) => void;
}

export default ({
	action,
	updateStore,
	elementIndex,
	id,
	value = { logic: [], dataMap: [], allData: false },
	validation,
	updateFormBusinessLogicValidation,
	addAlert,
}: Props) => {
	const { t } = useTranslation();

	// State for view
	const [isOpen, setIsOpen] = useState(false);
	const [isValid, setIsValid] = useState(true);

	const validate = useCallback(() => {
		isBusinessLogicValid(value) ? setIsValid(true) : setIsValid(false);
	}, [value]);

	// Amend updateStore to only accept value
	const handleUpdateStore = useCallback(
		(value: businessLogicValue) => {
			updateStore({
				elementIndex,
				property: action,
				value,
			});
		},
		[action, elementIndex, updateStore]
	);

	// Deals with validating form business logic
	useEffect(() => {
		if (action === "onLoad" || action === "beforeSave")
			runFormBusinessLogicValidation({
				value,
				action,
				updateFormBusinessLogicValidation,
			});
	}, [action, updateFormBusinessLogicValidation, value]);

	useEffect(() => {
		validate();
	}, [validate]);

	// Business Logic context props
	const businessLogicContextValue: BusinessLogicContextProps = useMemo(
		() => ({
			handleUpdateStore,
			elementIndex,
			id,
			value,
			action,
			validation,
			addAlert,
		}),
		[
			action,
			elementIndex,
			handleUpdateStore,
			id,
			addAlert,
			validation,
			value,
		]
	);

	return (
		<div
			className={`business-logic-card ${
				validation && !isValid ? " validation" : ""
			}
`}>
			<div className="business-logic-top">
				<div className="business-logic-heading">
					<div className="business-logic-heading__name">
						{t(`formDesigner.labels.${action}`)}
					</div>
				</div>
				<div>
					<IconButton
						icon="pencil"
						className={`${
							isOpen ? "btn-primary" : "btn-basic--primary"
						}`}
						label={
							isOpen
								? t("common.labels.close")
								: t("common.labels.edit")
						}
						onClick={() => setIsOpen(!isOpen)}
					/>
				</div>
			</div>
			{isOpen && (
				<BusinessLogicContext.Provider
					value={businessLogicContextValue}
					key={`${action}-${elementIndex}-context`}>
					<BusinessLogic />
				</BusinessLogicContext.Provider>
			)}
		</div>
	);
};
